import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { formatPercentage } from '@chilecompra/react-kit'
import { DataTable, Select, Switch, useSelect, useSwitch } from '@chilecompra/react-kit/components'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetProviderPurchasingMechanismsThunk } from './ProviderPurchasingMechanismsSection.actions'
import HorizontalBarChart from '../../components/Charts/HorizontalBarChart'
import RegionalPurchasingMechanisms from '../../components/Infographics/RegionalPurchasingMechanisms'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Description,
  GridContainer,
  GridItem,
  Info,
  Italic,
  Root,
  Title
} from './ProviderPurchasingMechanismsSection.styles'

/**
 * The ProviderPurchasingMechanismsSection's container.
 */
const ProviderPurchasingMechanismsSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { endYear, latestUpdate, startYear } = useSelector(state => state.topNavbar)
  const { dni } = useSelector(state => state.providerSection)
  const { categories, csv, data, loading } = useSelector(state => state.providerPurchasingMechanismsSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => dispatch(onGetProviderPurchasingMechanismsThunk({ dni, year: updatedValue })),
    overwriteCallback: () => endYear
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('sector-purchasing-mechanisms-horizontal-bar-chart', `Info_Sector_Mecanismos_Compra_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Sector_Mecanismos_Compra_${yearValue}`)
  })
  const handleDataLabelFormatter = useCallback(function () {
    return `
      <strong>$${formatAmount(this.y)} (${formatPercentage(this.point.percentage)})</strong>
      <br />
      <span style="color: ${theme.palette.brand.black3};">
        ${formatAmount(this.point.orders)} órdenes de compra
      </span>
      <br />
    `
  }, [])
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const getEnhancedData = useCallback(
    (currency, collection = []) =>
      collection.map(datum => ({
        ...datum,
        color: theme.palette.brand.primary,
        y: currency ? datum.amountUSD : datum.amount
      })),
    []
  )

  useEffect(() => {
    if (!dni || !endYear) {
      return
    }

    dispatch(onGetProviderPurchasingMechanismsThunk({ dni, year: endYear }))
  }, [dni, endYear])

  return (
    <Root>
      <GridContainer padding="35px 0 23px">
        <GridItem xs={12}>
          <Title>Mecanismos de compra</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Revisa el monto transado y número de órdenes de compra asociado a cada mecanismo de compra utilizados por el
            proveedor según el año seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DataLayout
            disableCaptureImage={disableCaptureImage}
            disableDownloadFile={disableDownloadFile}
            header={
              <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                <GridItem md={2} xs={12}>
                  <Select
                    label="AÑO"
                    loading={loading}
                    options={yearsOptions}
                    value={yearValue}
                    onChange={handleChangeYear}
                  />
                </GridItem>
                <GridItem md={2} xs={12}>
                  <Switch
                    checked={currencyValue}
                    color="primary"
                    labelLeft="CLP"
                    labelRight="USD"
                    onChange={handleChangeCurrency}
                  />
                </GridItem>
              </GridContainer>
            }
            infographic={<RegionalPurchasingMechanisms />}
            noData={data.length === 0}
            ref={dataLayoutRef}
            updatedDate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onChangeView={handleChangeView}
            onDownloadFile={handleDownloadFile}
          >
            <DataLayoutChild view={DataLayoutViews.CHART}>
              <GridContainer>
                <GridItem xs={12}>
                  <HorizontalBarChart
                    categories={categories}
                    data={getEnhancedData(currencyValue, data)}
                    dataLabelFormatter={handleDataLabelFormatter}
                    id="regional-purchasing-mechanisms-horizontal-bar-chart"
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="19px 0 0">
                <GridItem xs={12}>
                  <Info>{!currencyValue ? 'Millones de pesos (CLP)' : ' Miles de dólares (USD)'}</Info>
                </GridItem>
              </GridContainer>
              <GridContainer margin="19px 0 0">
                <GridItem xs={12}>
                  <Info>
                    <Italic>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Italic>
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
            <DataLayoutChild view={DataLayoutViews.TABLE}>
              <GridContainer padding="0 0 24px">
                <GridItem xs={12}>
                  <DataTable
                    headers={[
                      { label: 'Mecanismo de compra', value: 'name' },
                      { label: 'Nº de órdenes de compra', value: 'orders' },
                      { label: 'Monto total', value: 'amount' },
                      { label: 'Monto total ($USD)', value: 'amountUSD' }
                    ]}
                    headersCellsProps={[
                      { enableSort: true, width: '50%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true },
                      { enableSort: true }
                    ]}
                    initialRows={6}
                    loading={loading}
                    records={getEnhancedData(currencyValue, data)}
                    recordsDecorators={[
                      ({ name }) => name,
                      ({ orders }) => formatAmount(orders),
                      ({ amount }) => `$${formatAmount(amount)}`,
                      ({ amountUSD }) => `$${formatAmount(amountUSD)}`
                    ]}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="30px 0 0">
                <GridItem xs={12}>
                  <Info>
                    <Italic>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Italic>
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
          </DataLayout>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default ProviderPurchasingMechanismsSection
